import React, { useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import {
    Box,
    Button,
    ButtonGroup,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Switch,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
    startDateAtom,
    frequencyAtom,
    intervalAtom,
    weekdaysAtom,
    monthlyModeAtom,
    countAtom,
    rruleAtom,
    rfcRRuleAtom,
    days,
    endModeAtom,
    endDateAtom,
    daysMonFirst
} from "../atoms/recurringSettings";
import { setDetailAtom, setUpcomingSessionsDetailAtom } from "../atoms/booking";

function RRuleSelector({ packageView, submitting, checked, onChange }) {
    const theme = useTheme();
    const [startDate, setStartDate] = useAtom(startDateAtom);
    const [frequency, setFrequency] = useAtom(frequencyAtom);
    const [interval, setInterval] = useAtom(intervalAtom);
    const [weekdays, setWeekdays] = useAtom(weekdaysAtom);
    const [monthlyMode, setMonthlyMode] = useAtom(monthlyModeAtom);
    const [count, setCount] = useAtom(countAtom);
    const [endMode, setEndMode] = useAtom(endModeAtom);
    const [endDate, setEndDate] = useAtom(endDateAtom);
    const [rrule] = useAtom(rfcRRuleAtom);
    const [disabled, setDisabled] = useState(false);
    const [, setUpcomingSessionsDetail] = useAtom(
        setUpcomingSessionsDetailAtom
    );

    const daysStyleFunction = (val) => {
        return {
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: val ? "primary.main" : "white",
            borderStyle: "solid",
            borderWidth: "1px",
            borderColor: val && !disabled ? "primary.main" : "rgb(0,0,0,0.23)",
            minWidth: 0,
            color: val ? "white" : "primary.main",
            "&:hover": {
                backgroundColor: "primary.main",
                borderColor: "primary.main",
                color: "white"
            },
            "&.Mui-disabled": {
                borderColor: theme.palette.text.disabled
            }
        };
    };

    const handleWeekdayToggle = (dayIndex) => {
        setWeekdays((prev) => {
            //if trying to remove the last day, return the current state unchanged
            if (prev.length === 1 && prev.includes(dayIndex)) {
                return prev;
            }

            return prev.includes(dayIndex)
                ? prev.filter((d) => d !== dayIndex)
                : [...prev, dayIndex];
        });
        setUpcomingSessionsDetail(null);
    };

    const handleIntervalChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value > 0) {
            setInterval(value);
            setUpcomingSessionsDetail(null);
        }
    };

    const handleFrequencyChange = (e) => {
        setFrequency(e.target.value);
        setUpcomingSessionsDetail(null);
    };

    const handleCountChange = (e) => {
        const value = parseInt(e.target.value, 10);
        setCount(isNaN(value) ? null : value);
        setUpcomingSessionsDetail(null);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
        setUpcomingSessionsDetail(null);
    };

    const handleEndModeChange = (e) => {
        setEndMode(e.target.value);
        if (e.target.value === "endDate") {
            const threeMonthsLater = new Date(startDate);
            threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
            setEndDate(threeMonthsLater);
        }
        setUpcomingSessionsDetail(null);
    };

    const handleMonthlyModeChange = (e) => {
        setMonthlyMode(e.target.value);
        setUpcomingSessionsDetail(null);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid
                container
                sx={{ flexDirection: "row", display: "flex", gap: 1 }}
            >
                <Grid
                    item
                    xs={12}
                    sx={{
                        display: "flex",
                        gap: 2.25,
                        flexDirection: "column"
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: "11px",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            width: "100%"
                        }}
                    >
                        <FormControl sx={{ padding: "8px 0px" }}>
                            <FormControlLabel
                                sx={{
                                    "& .MuiFormControlLabel-label": {
                                        minWidth: "74px"
                                    }
                                }}
                                control={
                                    <Switch
                                        name="switch-repeating"
                                        checked={checked}
                                        onChange={onChange}
                                        disableRipple
                                        disabled={submitting}
                                        value={true}
                                        color="primary"
                                        inputProps={{
                                            "aria-label": "primary checkbox"
                                        }}
                                    />
                                }
                                label={
                                    packageView
                                        ? "Repeat every"
                                        : checked
                                          ? "Recur every"
                                          : "Recurring bookings"
                                }
                            />
                        </FormControl>
                        {(checked || packageView) && (
                            <>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={interval}
                                    onChange={handleIntervalChange}
                                    inputProps={{
                                        min: 1,
                                        style: {
                                            width: "60px",
                                            textAlign: "center"
                                        }
                                    }}
                                    sx={{ width: "60px" }}
                                    disabled={!checked || submitting}
                                />
                                {!packageView && (
                                    <FormControl
                                        sx={{
                                            width:
                                                frequency === "MONTHLY"
                                                    ? "122px"
                                                    : "112px"
                                        }}
                                        disabled={!checked || submitting}
                                    >
                                        <InputLabel id="frequency-select-label">
                                            {" "}
                                        </InputLabel>
                                        <Select
                                            labelId="frequency-select-label"
                                            id="frequency-select"
                                            value={frequency}
                                            label=""
                                            onChange={handleFrequencyChange}
                                            sx={{ minWidth: "101px" }}
                                        >
                                            <MenuItem value="WEEKLY">
                                                week{interval > 1 && "s"}
                                            </MenuItem>
                                            {!packageView && [
                                                <MenuItem
                                                    key="monthly"
                                                    value="MONTHLY"
                                                >
                                                    month
                                                    {interval > 1 && "s"}
                                                </MenuItem>,
                                                <MenuItem
                                                    key="yearly"
                                                    value="YEARLY"
                                                >
                                                    year
                                                    {interval > 1 && "s"}
                                                </MenuItem>
                                            ]}
                                        </Select>
                                    </FormControl>
                                )}
                                {frequency === "WEEKLY" && (
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        gap={1}
                                        sx={{ alignItems: "center" }}
                                    >
                                        <Typography variant="body1">
                                            {packageView
                                                ? `week${interval > 1 ? "s" : ""} on`
                                                : ""}{" "}
                                            {``}
                                        </Typography>
                                        <ButtonGroup
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "10px"
                                            }}
                                        >
                                            {daysMonFirst.map((day) => (
                                                <IconButton
                                                    key={day.id}
                                                    onClick={() =>
                                                        handleWeekdayToggle(
                                                            day.id
                                                        )
                                                    }
                                                    sx={daysStyleFunction(
                                                        weekdays.includes(
                                                            day.id
                                                        )
                                                    )}
                                                    disabled={
                                                        !checked || submitting
                                                    }
                                                >
                                                    <Box
                                                        sx={{
                                                            fontSize: "14px",
                                                            fontWeight: "500",
                                                            fontFamily: "roboto"
                                                        }}
                                                    >
                                                        {day.title}
                                                    </Box>
                                                </IconButton>
                                            ))}
                                        </ButtonGroup>
                                    </Box>
                                )}

                                {frequency === "MONTHLY" && (
                                    <FormControl
                                        component="fieldset"
                                        sx={{ maxWidth: "340px" }}
                                    >
                                        <RadioGroup
                                            value={monthlyMode}
                                            onChange={handleMonthlyModeChange}
                                            row
                                            sx={{ gap: "14px" }}
                                        >
                                            <FormControlLabel
                                                value="BYDATE"
                                                control={
                                                    <Radio disableRipple />
                                                }
                                                label={`Day ${startDate?.getDate()} of every month`}
                                            />
                                            <FormControlLabel
                                                value="BYDAY"
                                                control={
                                                    <Radio disableRipple />
                                                }
                                                label={`${getOrdinal(Math.ceil(startDate?.getDate() / 7))} ${getDayName(startDate)} of every month`}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                )}

                                {frequency === "YEARLY" && (
                                    <Typography>
                                        on{" "}
                                        {startDate.toLocaleDateString("en-US", {
                                            month: "long",
                                            day: "numeric"
                                        })}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                    {/* </Grid>*/}
                    {!packageView && checked && (
                        <FormControl
                            component="fieldset"
                            sx={{ width: "100%" }}
                        >
                            <RadioGroup
                                value={endMode}
                                onChange={handleEndModeChange}
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}
                                disabled={!checked || submitting}
                            >
                                <Grid item sx={{ display: "flex" }}>
                                    <FormControlLabel
                                        value="indefinitely"
                                        control={
                                            <Radio
                                                disableRipple
                                                disabled={submitting}
                                            />
                                        }
                                        label="Indefinitely"
                                        sx={{ mr: 0 }}
                                    />
                                </Grid>
                                <Grid item sx={{ display: "flex" }}>
                                    <FormControlLabel
                                        value="endDate"
                                        control={
                                            <Radio
                                                disableRipple
                                                disabled={submitting}
                                            />
                                        }
                                        label="Until"
                                    />
                                    <DatePicker
                                        value={endDate}
                                        renderInput={(props) => (
                                            <TextField {...props} />
                                        )}
                                        minDate={startDate}
                                        sx={{ width: "45%" }}
                                        onChange={handleEndDateChange}
                                        openPickerIcon={null}
                                        disabled={
                                            endMode !== "endDate" || submitting
                                        }
                                    />
                                </Grid>
                                <Grid
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "end"
                                    }}
                                >
                                    <FormControlLabel
                                        value="count"
                                        control={
                                            <Radio
                                                disableRipple
                                                disabled={submitting}
                                            />
                                        }
                                        label={`For a total of`}
                                    />
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={count || 1}
                                        onChange={handleCountChange}
                                        inputProps={{
                                            min: 1,
                                            style: {
                                                width: "60px",
                                                textAlign: "center"
                                            }
                                        }}
                                        sx={{ width: "60px" }}
                                        disabled={
                                            endMode !== "count" || submitting
                                        }
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{ ml: 1.5 }}
                                    >
                                        sessions
                                    </Typography>
                                </Grid>
                            </RadioGroup>
                        </FormControl>
                    )}
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
}

//helpers
function getOrdinal(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function getDayName(date) {
    return date.toLocaleDateString("en-US", { weekday: "long" });
}

export default RRuleSelector;
